"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ValidateManifestDriver = void 0;
const tslib_1 = require("tslib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const lib_1 = require("@feathersjs/hooks/lib");
const typedi_1 = require("typedi");
const os_1 = require("os");
const lodash_1 = require("lodash");
const wrapUtil_1 = require("../util/wrapUtil");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const telemetry_1 = require("./utils/telemetry");
const results_1 = require("./results");
const errors_1 = require("./errors");
const ManifestUtils_1 = require("./utils/ManifestUtils");
const localizeUtils_1 = require("../../../common/localizeUtils");
const constants_1 = require("../../../common/constants");
const common_1 = require("../../utils/common");
const constant_1 = require("../../configManager/constant");
const common_2 = require("../../../error/common");
const actionName = "teamsApp/validateManifest";
let ValidateManifestDriver = class ValidateManifestDriver {
    constructor() {
        this.description = localizeUtils_1.getLocalizedString("driver.teamsApp.description.validateDriver");
        this.progressTitle = localizeUtils_1.getLocalizedString("plugins.appstudio.validateManifest.progressBar.message");
    }
    async execute(args, context) {
        const wrapContext = new wrapUtil_1.WrapDriverContext(context, actionName, actionName);
        const res = await this.validate(args, wrapContext);
        return {
            result: res,
            summaries: wrapContext.summaries,
        };
    }
    async validate(args, context) {
        var _a, _b, _c, _d;
        telemetry_1.TelemetryUtils.init(context);
        const result = this.validateArgs(args);
        if (result.isErr()) {
            return teamsfx_api_1.err(result.error);
        }
        const manifestRes = await ManifestUtils_1.manifestUtils.getManifestV3(common_1.getAbsolutePath(args.manifestPath, context.projectPath), context);
        if (manifestRes.isErr()) {
            return teamsfx_api_1.err(manifestRes.error);
        }
        const manifest = manifestRes.value;
        let validationResult;
        if (manifest.$schema) {
            try {
                validationResult = await teamsfx_api_1.ManifestUtil.validateManifest(manifest);
            }
            catch (e) {
                return teamsfx_api_1.err(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                    localizeUtils_1.getLocalizedString("error.appstudio.validateFetchSchemaFailed", manifest.$schema, e.message),
                ]), constants_1.HelpLinks.WhyNeedProvision));
            }
        }
        else {
            return teamsfx_api_1.err(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, errors_1.AppStudioError.ValidationFailedError.message([
                localizeUtils_1.getLocalizedString("error.appstudio.validateSchemaNotDefined"),
            ]), constants_1.HelpLinks.WhyNeedProvision));
        }
        if (validationResult.length > 0) {
            const summaryStr = localizeUtils_1.getLocalizedString("driver.teamsApp.summary.validate.failed", validationResult.length);
            if (context.platform === teamsfx_api_1.Platform.CLI) {
                const outputMessage = [
                    {
                        content: "Teams Toolkit has checked manifest with its schema:\n\nSummary: \n",
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    },
                    {
                        content: `${validationResult.length} failed.\n`,
                        color: teamsfx_api_1.Colors.BRIGHT_RED,
                    },
                    {
                        content: localizeUtils_1.getDefaultString("driver.teamsApp.summary.validateManifest.checkPath", args.manifestPath),
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    },
                ];
                validationResult.map((error) => {
                    outputMessage.push({ content: `${constant_1.SummaryConstant.Failed} `, color: teamsfx_api_1.Colors.BRIGHT_RED });
                    outputMessage.push({
                        content: `${error}\n`,
                        color: teamsfx_api_1.Colors.BRIGHT_WHITE,
                    });
                });
                (_a = context.ui) === null || _a === void 0 ? void 0 : _a.showMessage("info", outputMessage, false);
            }
            else {
                // logs in output window
                const errors = validationResult
                    .map((error) => {
                    return `${constant_1.SummaryConstant.Failed} ${error}`;
                })
                    .join(os_1.EOL);
                const outputMessage = os_1.EOL +
                    localizeUtils_1.getLocalizedString("driver.teamsApp.summary.validateManifest", summaryStr, localizeUtils_1.getLocalizedString("driver.teamsApp.summary.validateManifest.checkPath", args.manifestPath), errors);
                (_b = context.logProvider) === null || _b === void 0 ? void 0 : _b.info(outputMessage);
            }
            lodash_1.merge(context.telemetryProperties, {
                [telemetry_1.TelemetryPropertyKey.validationErrors]: validationResult
                    .map((r) => r.replace(/\//g, ""))
                    .join(";"),
            });
            return teamsfx_api_1.err(results_1.AppStudioResultFactory.UserError(errors_1.AppStudioError.ValidationFailedError.name, [
                localizeUtils_1.getDefaultString("driver.teamsApp.validate.result", summaryStr),
                localizeUtils_1.getLocalizedString("driver.teamsApp.validate.result.display", summaryStr),
            ]));
        }
        else {
            // logs in output window
            const summaryStr = localizeUtils_1.getLocalizedString("driver.teamsApp.summary.validate.succeed", localizeUtils_1.getLocalizedString("driver.teamsApp.summary.validate.all"));
            const outputMessage = os_1.EOL + localizeUtils_1.getLocalizedString("driver.teamsApp.summary.validateManifest", summaryStr, "", "");
            (_c = context.logProvider) === null || _c === void 0 ? void 0 : _c.info(outputMessage);
            const validationSuccess = localizeUtils_1.getLocalizedString("driver.teamsApp.validate.result.display", summaryStr);
            if (context.platform === teamsfx_api_1.Platform.VS) {
                context.logProvider.info(validationSuccess);
            }
            if (args.showMessage) {
                if (context.platform === teamsfx_api_1.Platform.CLI) {
                    const outputMessage = [
                        {
                            content: "Teams Toolkit has completed checking your app package against validation rules. " +
                                summaryStr +
                                ".",
                            color: teamsfx_api_1.Colors.BRIGHT_GREEN,
                        },
                    ];
                    context.logProvider.info(outputMessage);
                }
                else {
                    (_d = context.ui) === null || _d === void 0 ? void 0 : _d.showMessage("info", validationSuccess, false);
                }
            }
            return teamsfx_api_1.ok(new Map());
        }
    }
    validateArgs(args) {
        if (!args || !args.manifestPath) {
            return teamsfx_api_1.err(new common_2.InvalidActionInputError(actionName, ["manifestPath"], "https://aka.ms/teamsfx-actions/teamsapp-validate"));
        }
        return teamsfx_api_1.ok(undefined);
    }
};
tslib_1.__decorate([
    lib_1.hooks([addStartAndEndTelemetry_1.addStartAndEndTelemetry(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, wrapUtil_1.WrapDriverContext]),
    tslib_1.__metadata("design:returntype", Promise)
], ValidateManifestDriver.prototype, "validate", null);
ValidateManifestDriver = tslib_1.__decorate([
    typedi_1.Service(actionName)
], ValidateManifestDriver);
exports.ValidateManifestDriver = ValidateManifestDriver;
//# sourceMappingURL=validate.js.map